/**
 * Safe Fetch Utility
 * 
 * This utility provides safe HTTP request handling that:
 * 1. Prevents "body stream already read" errors by reading response once
 * 2. Handles postMessage cloning errors from monitoring scripts
 * 3. Provides consistent error handling across all auth endpoints
 */

// Store the original fetch before any interceptors modify it
const originalFetch = window.fetch.bind(window);

/**
 * Parse response body safely - reads the body ONCE only
 * This prevents "body stream already read" errors
 * 
 * @param {Response} res - Fetch Response object
 * @returns {Promise<{ok: boolean, status: number, data: any, raw: string}>}
 */
export async function parseResponse(res) {
  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  
  // Read the body ONCE only as text
  let raw = "";
  try {
    raw = await res.text();
  } catch (e) {
    // Body may have already been read by an interceptor
    console.warn("[parseResponse] Could not read response body:", e.message);
    raw = "";
  }
  
  // Parse JSON if applicable
  let data = null;
  if (isJson && raw) {
    try {
      data = JSON.parse(raw);
    } catch (e) {
      // Not valid JSON, keep data as null
      data = null;
    }
  }
  
  return {
    ok: res.ok,
    status: res.status,
    statusText: res.statusText,
    data,
    raw
  };
}

/**
 * Safe fetch that handles monitoring script errors
 * @param {string|Request} input - URL or Request object
 * @param {RequestInit} init - Fetch options
 * @returns {Promise<Response>}
 */
export async function safeFetch(input, init = {}) {
  try {
    const response = await originalFetch(input, init);
    return response;
  } catch (error) {
    // Check if this is a postMessage cloning error
    if (error.name === 'DataCloneError' || 
        (error.message && error.message.includes('could not be cloned'))) {
      console.warn('[safeFetch] Caught DataCloneError, retrying with XMLHttpRequest');
      return await cleanFetch(input, init);
    }
    throw error;
  }
}

/**
 * Safe fetch that returns parsed response directly
 * Use this for auth endpoints to avoid double-reading body
 * 
 * @param {string} url - Request URL
 * @param {RequestInit} init - Fetch options
 * @returns {Promise<{ok: boolean, status: number, data: any, raw: string}>}
 */
export async function safeFetchJson(url, init = {}) {
  try {
    const response = await originalFetch(url, init);
    return await parseResponse(response);
  } catch (error) {
    // Check if this is a postMessage cloning error
    if (error.name === 'DataCloneError' || 
        (error.message && error.message.includes('could not be cloned'))) {
      console.warn('[safeFetchJson] Caught DataCloneError, retrying with XMLHttpRequest');
      const response = await cleanFetch(url, init);
      return await parseResponse(response);
    }
    
    // Network error or other fetch failure
    return {
      ok: false,
      status: 0,
      statusText: 'Network Error',
      data: null,
      raw: error.message || 'Request failed'
    };
  }
}

/**
 * Clean fetch using XMLHttpRequest as fallback
 * Bypasses any fetch interceptors
 */
async function cleanFetch(input, init = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    const url = typeof input === 'string' ? input : input.url;
    const method = init.method || 'GET';
    
    xhr.open(method, url, true);
    
    // Set headers
    const headers = init.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    
    // Handle credentials
    if (init.credentials === 'include') {
      xhr.withCredentials = true;
    }
    
    xhr.onload = function() {
      // Create a Response-like object
      const response = new Response(xhr.responseText, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: parseXHRHeaders(xhr.getAllResponseHeaders())
      });
      resolve(response);
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    xhr.send(init.body || null);
  });
}

/**
 * Parse XHR headers string into Headers object
 */
function parseXHRHeaders(headerStr) {
  const headers = new Headers();
  if (!headerStr) return headers;
  
  headerStr.split('\r\n').forEach(line => {
    const idx = line.indexOf(': ');
    if (idx > 0) {
      headers.append(line.substring(0, idx), line.substring(idx + 2));
    }
  });
  
  return headers;
}

/**
 * Install global error handler for postMessage errors
 * Prevents monitoring script errors from breaking the app
 */
export function installPostMessageErrorHandler() {
  if (typeof window === 'undefined') return;
  
  const originalPostMessage = window.postMessage.bind(window);
  
  window.postMessage = function(message, targetOrigin, transfer) {
    try {
      return originalPostMessage(message, targetOrigin, transfer);
    } catch (error) {
      if (error.name === 'DataCloneError' || 
          (error.message && error.message.includes('could not be cloned'))) {
        // Silently suppress cloning errors from monitoring scripts
        return;
      }
      throw error;
    }
  };
  
  // Handle parent.postMessage if in iframe
  if (window.parent && window.parent !== window) {
    try {
      const originalParentPostMessage = window.parent.postMessage.bind(window.parent);
      window.parent.postMessage = function(message, targetOrigin, transfer) {
        try {
          return originalParentPostMessage(message, targetOrigin, transfer);
        } catch (error) {
          if (error.name === 'DataCloneError' || 
              (error.message && error.message.includes('could not be cloned'))) {
            return;
          }
          throw error;
        }
      };
    } catch (e) {
      // Cross-origin access denied - that's expected
    }
  }
}

export default safeFetch;
